import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import '../SupplierDashboard/dashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState('overview');
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierDetails, setShowSupplierDetails] = useState(false);
  const [customerRequests, setCustomerRequests] = useState({
    pending: [],
    processing: [],
    completed: [],
  });
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [activeStatusTab, setActiveStatusTab] = useState('pending');

  useEffect(() => {
    // Check authentication on component mount
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    
    if (!token || !userData) {
      navigate('/admin-login');
      return;
    }
    
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
    
    // Fetch updated user data
    fetchUser();
  }, [navigate]);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/user`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success && data.data) {
        setUser(data.data);
        localStorage.setItem('admin_user', JSON.stringify(data.data));
      } else {
        navigate('/admin-login');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      navigate('/admin-login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      if (token) {
        await fetch(`${API_BASE_URL}/admin/logout`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin-login');
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/suppliers`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        setSuppliers(data.data || []);
      } else {
        console.error('Failed to fetch suppliers:', data.message);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const handleViewSupplierDetails = async (supplier) => {
    console.log('Viewing supplier details:', supplier);
    
    if (!supplier || !supplier.id) {
      alert('خطأ: بيانات المورد غير صحيحة');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      console.log('Fetching supplier details for ID:', supplier.id);
      const response = await fetch(`${API_BASE_URL}/admin/suppliers/${supplier.id}`, {
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Supplier details response:', data);

      if (data.success) {
        console.log('Setting supplier details:', data.data);
        setSelectedSupplier(data.data);
        setShowSupplierDetails(true);
      } else {
        console.error('Failed to fetch supplier details:', data.message);
        alert('فشل تحميل تفاصيل المورد: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching supplier details:', err);
      alert('حدث خطأ أثناء تحميل تفاصيل المورد: ' + err.message);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المورد؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        alert('تم حذف المورد بنجاح');
        fetchSuppliers(); // Refresh list
      } else {
        alert(data.message || 'فشل حذف المورد');
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
      alert('حدث خطأ أثناء حذف المورد');
    }
  };

  const fetchCustomerRequests = async () => {
    try {
      setRequestsLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/customer-requests`, {
        headers: headers,
      });

      const data = await response.json();

      if (data.success) {
        console.log('Customer requests data:', data.data);
        setCustomerRequests({
          pending: Array.isArray(data.data.pending) ? data.data.pending : [],
          processing: Array.isArray(data.data.processing) ? data.data.processing : [],
          completed: Array.isArray(data.data.completed) ? data.data.completed : [],
        });
      } else {
        console.error('Failed to fetch customer requests:', data.message);
        setCustomerRequests({
          pending: [],
          processing: [],
          completed: [],
        });
      }
    } catch (err) {
      console.error('Error fetching customer requests:', err);
      setCustomerRequests({
        pending: [],
        processing: [],
        completed: [],
      });
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleViewRequestDetails = async (request) => {
    console.log('handleViewRequestDetails called with request:', request);
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      console.log('Fetching request details for ID:', request.id);
      const response = await fetch(`${API_BASE_URL}/admin/customer-requests/${request.id}`, {
        headers: headers,
      });

      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        console.log('Setting selectedRequest and showRequestDetails to true');
        setSelectedRequest(data.data);
        setShowRequestDetails(true);
        console.log('Modal should be visible now');
      } else {
        console.error('Failed to fetch request details:', data.message);
        alert('فشل تحميل تفاصيل الطلب: ' + (data.message || ''));
      }
    } catch (err) {
      console.error('Error fetching request details:', err);
      alert('حدث خطأ أثناء تحميل تفاصيل الطلب');
    }
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin-login');
        return;
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/customer-requests/${requestId}/status`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (data.success) {
        alert('تم تحديث حالة الطلب بنجاح');
        fetchCustomerRequests(); // Refresh list
        if (showRequestDetails) {
          setShowRequestDetails(false);
        }
      } else {
        alert(data.message || 'فشل تحديث حالة الطلب');
      }
    } catch (err) {
      console.error('Error updating request status:', err);
      alert('حدث خطأ أثناء تحديث حالة الطلب');
    }
  };

  useEffect(() => {
    if (activeMenu === 'suppliers') {
      fetchSuppliers();
    } else if (activeMenu === 'requests') {
      fetchCustomerRequests();
    }
  }, [activeMenu]);

  if (loading) {
    return (
      <div className="supplier-dashboard-wrapper">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-dashboard-wrapper">
      {/* Top Bar */}
      <div className="dashboard-topbar">
        <div className="topbar-content">
          <div className="topbar-left">
            <h2>
              <i className="fas fa-user-shield"></i>
              لوحة تحكم الإدمن
            </h2>
          </div>
          <div className="topbar-right">
            <div className="topbar-user">
              <i className="fas fa-user"></i>
              <span>{user?.name || 'Admin'}</span>
            </div>
            <button onClick={handleLogout} className="topbar-logout" title="تسجيل الخروج">
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Container */}
      <div className="dashboard-container">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <ul className="sidebar-menu">
            <li className={activeMenu === 'overview' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('overview'); }}>
                <i className="fas fa-chart-line"></i>
                <span>نظرة عامة</span>
              </a>
            </li>
            <li className={activeMenu === 'suppliers' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('suppliers'); }}>
                <i className="fas fa-users"></i>
                <span>الموردون</span>
              </a>
            </li>
            <li className={activeMenu === 'requests' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('requests'); }}>
                <i className="fas fa-clipboard-list"></i>
                <span>طلبات العملاء</span>
              </a>
            </li>
            <li className={activeMenu === 'quotes' ? 'active' : ''}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveMenu('quotes'); }}>
                <i className="fas fa-file-invoice-dollar"></i>
                <span>عروض الأسعار</span>
              </a>
            </li>
          </ul>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {activeMenu === 'overview' && (
            <div>
              <h2>نظرة عامة</h2>
              <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <p>مرحباً بك في لوحة تحكم الإدمن</p>
                <p>هنا يمكنك إدارة الموردين وطلبات العملاء وعروض الأسعار</p>
              </div>
            </div>
          )}

          {activeMenu === 'suppliers' && (
            <div>
              <h2>الموردون ({suppliers.length})</h2>
              
              {suppliersLoading ? (
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>جاري التحميل...</p>
                </div>
              ) : suppliers.length === 0 ? (
                <div style={{ background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center', color: '#718096' }}>
                  <i className="fas fa-users" style={{ fontSize: '48px', marginBottom: '15px' }}></i>
                  <p>لا يوجد موردون حالياً</p>
                </div>
              ) : (
                <div className="requests-grid">
                  {suppliers.map(supplier => (
                    <div key={supplier.id} className="request-card">
                      <h3>{supplier.name || 'بدون اسم'}</h3>
                      {supplier.email && (
                        <p><strong>البريد:</strong> {supplier.email}</p>
                      )}
                      {supplier.phone_number && (
                        <p><strong>الهاتف:</strong> {supplier.phone_number}</p>
                      )}
                      {supplier.geographic_range && (
                        <p><strong>النطاق الجغرافي:</strong> {supplier.geographic_range}</p>
                      )}
                      {supplier.main_office_address && (
                        <p><strong>عنوان المكتب:</strong> {supplier.main_office_address}</p>
                      )}
                      <p style={{ fontSize: '12px', color: '#718096', marginTop: '10px' }}>
                        <strong>تاريخ التسجيل:</strong> {new Date(supplier.created_at).toLocaleDateString('ar-SA')}
                      </p>
                      
                      <div className="card-actions">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewSupplierDetails(supplier);
                          }}
                          className="btn-primary"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                          <i className="fas fa-eye"></i> التفاصيل
                        </button>
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteSupplier(supplier.id);
                          }}
                          className="btn-delete"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        >
                          <i className="fas fa-trash"></i> حذف
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Supplier Details Modal */}
          {showSupplierDetails && selectedSupplier && (
            <div 
              className="modal" 
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2000 
              }} 
              onClick={(e) => {
                // Close modal if clicking on the backdrop (not the content)
                if (e.target === e.currentTarget) {
                  setShowSupplierDetails(false);
                }
              }}
            >
              <div 
                className="modal-content" 
                style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  maxWidth: '900px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  zIndex: 2001,
                  position: 'relative'
                }} 
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, color: '#2d3748' }}>
                    <i className="fas fa-user"></i> تفاصيل المورد
                  </h2>
                  <button 
                    onClick={() => setShowSupplierDetails(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#718096',
                      padding: '5px 10px',
                      lineHeight: '1',
                      fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#2d3748'}
                    onMouseOut={(e) => e.target.style.color = '#718096'}
                  >
                    ×
                  </button>
                </div>

                {/* Supplier Info */}
                <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '18px' }}>
                    <i className="fas fa-user"></i> معلومات المورد
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <p><strong>الاسم:</strong> {selectedSupplier.name || 'بدون اسم'}</p>
                    {selectedSupplier.email && (
                      <p><strong>البريد:</strong> {selectedSupplier.email}</p>
                    )}
                    {selectedSupplier.phone_number && (
                      <p><strong>الهاتف:</strong> {selectedSupplier.phone_number}</p>
                    )}
                    {selectedSupplier.geographic_range && (
                      <p><strong>النطاق الجغرافي:</strong> {selectedSupplier.geographic_range}</p>
                    )}
                    {selectedSupplier.main_office_address && (
                      <p><strong>عنوان المكتب:</strong> {selectedSupplier.main_office_address}</p>
                    )}
                    <p><strong>تاريخ التسجيل:</strong> {new Date(selectedSupplier.created_at).toLocaleDateString('ar-SA')}</p>
                  </div>
                </div>

                {/* Materials */}
                {selectedSupplier.materials && selectedSupplier.materials.length > 0 && (
                  <div style={{ background: '#f0fff4', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #9ae6b4' }}>
                    <h3 style={{ color: '#48bb78', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-boxes"></i> المواد ({selectedSupplier.materials.length})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                      {selectedSupplier.materials.map(material => (
                        <div key={material.id} style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            {material.material_type === 'panel' ? 'لوح' : material.material_type === 'battery' ? 'بطارية' : 'محول'}
                            {material.brand && ` - ${material.brand}`}
                          </p>
                          {material.material_type === 'panel' && (
                            <>
                              {material.panel_size_sqm && <p style={{ fontSize: '13px' }}>القياس: {material.panel_size_sqm} م²</p>}
                              {material.panel_output_watts && <p style={{ fontSize: '13px' }}>الإنتاجية: {material.panel_output_watts} واط</p>}
                              {material.panel_price && <p style={{ fontSize: '13px' }}>السعر: ${material.panel_price}</p>}
                              {material.available_quantity !== undefined && <p style={{ fontSize: '13px' }}>المتوفر: {material.available_quantity}</p>}
                            </>
                          )}
                          {material.material_type === 'battery' && (
                            <>
                              {material.battery_capacity_kwh && <p style={{ fontSize: '13px' }}>السعة: {material.battery_capacity_kwh} كيلو واط/ساعة</p>}
                              {material.battery_dod_percentage && <p style={{ fontSize: '13px' }}>عمق التفريغ: {(parseFloat(material.battery_dod_percentage) * 100).toFixed(1)}%</p>}
                              {material.panel_price && <p style={{ fontSize: '13px' }}>السعر: ${material.panel_price}</p>}
                              {material.available_quantity !== undefined && <p style={{ fontSize: '13px' }}>المتوفر: {material.available_quantity}</p>}
                            </>
                          )}
                          {material.material_type === 'inverter' && (
                            <>
                              {material.inverter_capacity_kw && <p style={{ fontSize: '13px' }}>السعة: {material.inverter_capacity_kw} كيلو واط</p>}
                              {material.panel_price && <p style={{ fontSize: '13px' }}>السعر: ${material.panel_price}</p>}
                              {material.available_quantity !== undefined && <p style={{ fontSize: '13px' }}>المتوفر: {material.available_quantity}</p>}
                            </>
                          )}
                          <p style={{ fontSize: '12px', color: material.is_active ? '#48bb78' : '#e53e3e', marginTop: '8px' }}>
                            {material.is_active ? 'نشط' : 'غير نشط'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Services */}
                {selectedSupplier.additional_services && (
                  <div style={{ background: '#fffaf0', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fbd38d' }}>
                    <h3 style={{ color: '#ed8936', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-dollar-sign"></i> التكاليف الإضافية
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                      {selectedSupplier.additional_services.grounding_lightning_rod_price && (
                        <p><strong>التأريض ومانعة الصواعق:</strong> ${parseFloat(selectedSupplier.additional_services.grounding_lightning_rod_price).toFixed(2)}</p>
                      )}
                      {selectedSupplier.additional_services.fire_fighting_system_price && (
                        <p><strong>مكافحة الحريق:</strong> ${parseFloat(selectedSupplier.additional_services.fire_fighting_system_price).toFixed(2)}</p>
                      )}
                      {selectedSupplier.additional_services.installation_delivery_maintenance_price && (
                        <p><strong>التركيب والصيانة:</strong> ${parseFloat(selectedSupplier.additional_services.installation_delivery_maintenance_price).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Quotes Count */}
                {selectedSupplier.quotes && (
                  <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-file-invoice-dollar"></i> عروض الأسعار
                    </h3>
                    <p style={{ fontSize: '16px' }}>
                      <strong>عدد العروض:</strong> {selectedSupplier.quotes.length} عرض
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <button onClick={() => setShowSupplierDetails(false)} className="btn-secondary" style={{ padding: '10px 20px' }}>
                    إغلاق
                  </button>
                  <button 
                    onClick={() => {
                      if (window.confirm('هل أنت متأكد من حذف هذا المورد؟')) {
                        handleDeleteSupplier(selectedSupplier.id);
                        setShowSupplierDetails(false);
                      }
                    }} 
                    className="btn-delete" 
                    style={{ padding: '10px 20px' }}
                  >
                    <i className="fas fa-trash"></i> حذف المورد
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'requests' && (
            <div>
              <h2>طلبات العملاء</h2>
              
              {/* Status Tabs */}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '20px',
                background: 'white',
                padding: '10px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <button
                  onClick={() => setActiveStatusTab('pending')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: activeStatusTab === 'pending' ? '#667eea' : '#e2e8f0',
                    color: activeStatusTab === 'pending' ? 'white' : '#4a5568',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                >
                  <i className="fas fa-clock"></i> معلقة ({customerRequests.pending.length})
                </button>
                <button
                  onClick={() => setActiveStatusTab('processing')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: activeStatusTab === 'processing' ? '#ed8936' : '#e2e8f0',
                    color: activeStatusTab === 'processing' ? 'white' : '#4a5568',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                >
                  <i className="fas fa-cog"></i> قيد التنفيذ ({customerRequests.processing.length})
                </button>
                <button
                  onClick={() => setActiveStatusTab('completed')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: activeStatusTab === 'completed' ? '#48bb78' : '#e2e8f0',
                    color: activeStatusTab === 'completed' ? 'white' : '#4a5568',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                >
                  <i className="fas fa-check-circle"></i> تم تنفيذها ({customerRequests.completed.length})
                </button>
              </div>

              {requestsLoading ? (
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>جاري التحميل...</p>
                </div>
              ) : !customerRequests[activeStatusTab] || customerRequests[activeStatusTab].length === 0 ? (
                <div style={{ background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center', color: '#718096' }}>
                  <i className="fas fa-clipboard-list" style={{ fontSize: '48px', marginBottom: '15px' }}></i>
                  <p>لا توجد طلبات {activeStatusTab === 'pending' ? 'معلقة' : activeStatusTab === 'processing' ? 'قيد التنفيذ' : 'مكتملة'} حالياً</p>
                </div>
              ) : (
                <div className="requests-grid">
                  {(customerRequests[activeStatusTab] || []).map(request => (
                    <div key={request.id} className="request-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                        <h3>{request.full_name}</h3>
                        <span style={{
                          padding: '5px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: activeStatusTab === 'pending' ? '#fed7d7' : activeStatusTab === 'processing' ? '#feebc8' : '#c6f6d5',
                          color: activeStatusTab === 'pending' ? '#c53030' : activeStatusTab === 'processing' ? '#c05621' : '#22543d'
                        }}>
                          {activeStatusTab === 'pending' ? 'معلقة' : activeStatusTab === 'processing' ? 'قيد التنفيذ' : 'مكتملة'}
                        </span>
                      </div>
                      <p><strong>الهاتف:</strong> {request.phone_number}</p>
                      <p><strong>المساحة:</strong> {request.area_sqm} م²</p>
                      {request.required_system_size_kw && (
                        <p><strong>حجم المنظومة:</strong> {parseFloat(request.required_system_size_kw).toFixed(2)} كيلو واط</p>
                      )}
                      {activeStatusTab === 'pending' && request.available_quotes && Array.isArray(request.available_quotes) && request.available_quotes.length > 0 && (
                        <p style={{ color: '#48bb78', fontWeight: 'bold', marginTop: '10px' }}>
                          <i className="fas fa-list"></i> {request.available_quotes.length} عرض متوفر
                        </p>
                      )}
                      <p style={{ fontSize: '12px', color: '#718096', marginTop: '10px' }}>
                        <strong>التاريخ:</strong> {new Date(request.created_at).toLocaleDateString('ar-SA')}
                      </p>
                      
                      <div className="card-actions">
                        <button 
                          onClick={() => handleViewRequestDetails(request)}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-eye"></i> التفاصيل
                        </button>
                        {activeStatusTab === 'pending' && (
                          <button 
                            onClick={() => handleUpdateRequestStatus(request.id, 'processing')}
                            className="btn-primary"
                            style={{ flex: 1 }}
                          >
                            <i className="fas fa-play"></i> بدء التنفيذ
                          </button>
                        )}
                        {activeStatusTab === 'processing' && (
                          <button 
                            onClick={() => handleUpdateRequestStatus(request.id, 'completed')}
                            className="btn-primary"
                            style={{ flex: 1 }}
                          >
                            <i className="fas fa-check"></i> إكمال
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeMenu === 'quotes' && (
            <div>
              <h2>عروض الأسعار</h2>
              <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <p>قائمة عروض الأسعار قريباً...</p>
              </div>
            </div>
          )}

          {/* Customer Request Details Modal */}
          {showRequestDetails && selectedRequest && (
            <div 
              className="modal" 
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2000 
              }} 
              onClick={(e) => {
                if (e.target.classList.contains('modal')) {
                  setShowRequestDetails(false);
                }
              }}
            >
              <div 
                className="modal-content" 
                style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  maxWidth: '1000px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  zIndex: 2001,
                  position: 'relative'
                }} 
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                  <h2 style={{ margin: 0, color: '#2d3748' }}>
                    <i className="fas fa-clipboard-list"></i> تفاصيل الطلب #{selectedRequest.id}
                  </h2>
                  <button 
                    onClick={() => setShowRequestDetails(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '28px',
                      cursor: 'pointer',
                      color: '#718096',
                      padding: '5px 10px',
                      lineHeight: '1',
                      fontWeight: 'bold'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#2d3748'}
                    onMouseOut={(e) => e.target.style.color = '#718096'}
                  >
                    ×
                  </button>
                </div>

                {/* Customer Info */}
                <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '18px' }}>
                    <i className="fas fa-user"></i> معلومات العميل
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                    <p><strong>الاسم:</strong> {selectedRequest.full_name}</p>
                    <p><strong>الهاتف:</strong> {selectedRequest.phone_number}</p>
                    <p><strong>المساحة:</strong> {selectedRequest.area_sqm} م²</p>
                    <p><strong>الحالة:</strong> 
                      <span style={{
                        padding: '5px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginRight: '10px',
                        background: selectedRequest.status === 'pending' ? '#fed7d7' : selectedRequest.status === 'processing' ? '#feebc8' : '#c6f6d5',
                        color: selectedRequest.status === 'pending' ? '#c53030' : selectedRequest.status === 'processing' ? '#c05621' : '#22543d'
                      }}>
                        {selectedRequest.status === 'pending' ? 'معلقة' : selectedRequest.status === 'processing' ? 'قيد التنفيذ' : 'مكتملة'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Consumption Info */}
                {selectedRequest.daytime_consumption_amps && (
                  <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-bolt"></i> معلومات الاستهلاك
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <p><strong>الاستهلاك النهاري:</strong> {selectedRequest.daytime_consumption_amps} أمبير</p>
                      {selectedRequest.nightly_consumption_amps_per_hour && (
                        <p><strong>الاستهلاك الليلي:</strong> {selectedRequest.nightly_consumption_amps_per_hour} أمبير/ساعة</p>
                      )}
                      {selectedRequest.battery_hours_night && (
                        <p><strong>عدد ساعات الليل:</strong> {selectedRequest.battery_hours_night} ساعة</p>
                      )}
                    </div>
                  </div>
                )}

                {/* System Requirements */}
                {selectedRequest.required_system_size_kw && (
                  <div style={{ background: '#f7fafc', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px' }}>
                      <i className="fas fa-calculator"></i> متطلبات المنظومة
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <p style={{ fontSize: '16px', color: '#667eea', fontWeight: 'bold' }}>
                        <strong>حجم المنظومة المطلوب:</strong> {parseFloat(selectedRequest.required_system_size_kw).toFixed(2)} كيلو واط
                      </p>
                      {selectedRequest.required_battery_capacity && (
                        <p style={{ fontSize: '16px' }}>
                          <strong>سعة البطارية المطلوبة:</strong> {parseFloat(selectedRequest.required_battery_capacity).toFixed(2)} واط/ساعة
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Available Quotes (only for pending) */}
                {selectedRequest.status === 'pending' && selectedRequest.available_quotes && Array.isArray(selectedRequest.available_quotes) && selectedRequest.available_quotes.length > 0 && (
                  <div style={{ background: '#f0fff4', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #9ae6b4' }}>
                    <h3 style={{ color: '#48bb78', marginBottom: '20px', fontSize: '18px' }}>
                      <i className="fas fa-list"></i> العروض المتوفرة ({selectedRequest.available_quotes.length} عرض)
                    </h3>
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {(selectedRequest.available_quotes || []).slice(0, 10).map((quote, index) => (
                        <div key={index} style={{
                          background: index === 0 ? '#f0fff4' : 'white',
                          border: `2px solid ${index === 0 ? '#48bb78' : '#e2e8f0'}`,
                          borderRadius: '12px',
                          padding: '18px',
                          position: 'relative'
                        }}>
                          {index === 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '10px',
                              left: '10px',
                              background: '#48bb78',
                              color: 'white',
                              padding: '5px 15px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              الأفضل سعراً
                            </div>
                          )}
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: index === 0 ? '30px' : '0' }}>
                            <div>
                              <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>المورد: {quote.supplier_name}</p>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>اللوح:</strong> {quote.panel.brand} ({quote.panel.count} لوح)
                              </p>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>البطارية:</strong> {quote.battery.brand} ({quote.battery.count} بطارية)
                              </p>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>المحول:</strong> {quote.inverter.brand}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>الألواح:</strong> ${quote.panel.price.toFixed(2)}
                              </p>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>البطاريات:</strong> ${quote.battery.price.toFixed(2)}
                              </p>
                              <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                <strong>المحول:</strong> ${quote.inverter.price.toFixed(2)}
                              </p>
                              {quote.additional_services.grounding > 0 && (
                                <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                  <strong>التأريض:</strong> ${quote.additional_services.grounding.toFixed(2)}
                                </p>
                              )}
                              {quote.additional_services.fire_fighting > 0 && (
                                <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                  <strong>مكافحة الحريق:</strong> ${quote.additional_services.fire_fighting.toFixed(2)}
                                </p>
                              )}
                              {quote.additional_services.installation > 0 && (
                                <p style={{ fontSize: '13px', color: '#4a5568' }}>
                                  <strong>التركيب:</strong> ${quote.additional_services.installation.toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #667eea', textAlign: 'center' }}>
                            <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea', margin: 0 }}>
                              السعر الإجمالي: ${quote.total_price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                  <div>
                    {selectedRequest.status === 'pending' && (
                      <button 
                        onClick={() => {
                          handleUpdateRequestStatus(selectedRequest.id, 'processing');
                          setShowRequestDetails(false);
                        }}
                        className="btn-primary"
                        style={{ padding: '10px 20px' }}
                      >
                        <i className="fas fa-play"></i> بدء التنفيذ
                      </button>
                    )}
                    {selectedRequest.status === 'processing' && (
                      <button 
                        onClick={() => {
                          handleUpdateRequestStatus(selectedRequest.id, 'completed');
                          setShowRequestDetails(false);
                        }}
                        className="btn-primary"
                        style={{ padding: '10px 20px' }}
                      >
                        <i className="fas fa-check"></i> إكمال الطلب
                      </button>
                    )}
                  </div>
                  <button onClick={() => setShowRequestDetails(false)} className="btn-secondary" style={{ padding: '10px 20px' }}>
                    إغلاق
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;

